/**
 * PatientSummaryContext
 * =====================
 * Shared context for patient summary state (historical, realtime, clinical recommendation).
 *
 * Problem solved: RightPanelTabs is mounted in 4 simultaneous tab panels (Registration,
 * Pre-Consultation, Patient Visit, Prescription) via display:none tab switching. Each was
 * calling usePatientSummary independently, causing 4 simultaneous /realtime API hits every
 * 30 seconds. Moving state here ensures ONE request, ONE interval, data synced across all tabs.
 */
import { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { useUser } from './UserContext';
import { useSession } from './SessionContext';
import type {
  HistoricalSummary,
  RealtimeSummary,
  ClinicalRecommendationResponse,
} from '../hooks/usePatientSummary';

// ==========================================
// CONTEXT TYPE
// ==========================================

interface PatientSummaryContextValue {
  // Historical summary
  historicalSummary: HistoricalSummary | null;
  historicalLoading: boolean;
  historicalError: string | null;
  fetchHistoricalSummary: (forceRegenerate?: boolean) => Promise<void>;

  // Real-time summary
  realtimeSummary: RealtimeSummary | null;
  realtimeLoading: boolean;
  realtimeError: string | null;
  fetchRealtimeSummary: () => Promise<void>;

  // AI Clinical Recommendation (Sensei tab)
  clinicalRecommendation: ClinicalRecommendationResponse | null;
  clinicalLoading: boolean;
  clinicalError: string | null;
  fetchClinicalRecommendation: (visitId: number) => Promise<void>;
  generateClinicalRecommendation: (visitId: number, sessionId?: string) => Promise<void>;

  // Auto-refresh state
  isAutoRefreshing: boolean;

  /**
   * Called by RightPanelTabs when live or saved transcripts become available.
   * Gates the realtime summary auto-refresh so we don't hit the LLM with an empty transcript.
   */
  setHasTranscripts: (value: boolean) => void;
}

// ==========================================
// CONTEXT
// ==========================================

const PatientSummaryContext = createContext<PatientSummaryContextValue | undefined>(undefined);

const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds

// ==========================================
// PROVIDER
// ==========================================

export function PatientSummaryProvider({ children }: { children: ReactNode }) {
  const { patient } = useUser();
  const { session } = useSession();

  const patientId = patient ? String(patient.patient_id) : undefined;
  const sessionId = session?.session_id ?? undefined;
  const isSessionActive = !!session && session.status === 'active';

  // Historical summary state
  const [historicalSummary, setHistoricalSummary] = useState<HistoricalSummary | null>(null);
  const [historicalLoading, setHistoricalLoading] = useState(false);
  const [historicalError, setHistoricalError] = useState<string | null>(null);

  // Real-time summary state
  const [realtimeSummary, setRealtimeSummary] = useState<RealtimeSummary | null>(null);
  const [realtimeLoading, setRealtimeLoading] = useState(false);
  const [realtimeError, setRealtimeError] = useState<string | null>(null);

  // Clinical recommendation state
  const [clinicalRecommendation, setClinicalRecommendation] = useState<ClinicalRecommendationResponse | null>(null);
  const [clinicalLoading, setClinicalLoading] = useState(false);
  const [clinicalError, setClinicalError] = useState<string | null>(null);

  // Auto-refresh
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const [hasTranscripts, setHasTranscripts] = useState(false);

  // Single in-flight guard — no cross-instance problem since this is one provider instance
  const inFlightRef = useRef(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const prevSessionIdRef = useRef<string | undefined>(undefined);

  // ==========================================
  // API CALLS
  // ==========================================

  const fetchHistoricalSummary = useCallback(async (forceRegenerate = false) => {
    if (!patientId) return;
    setHistoricalLoading(true);
    setHistoricalError(null);
    try {
      const url = forceRegenerate
        ? `/api/patient-summary/${patientId}/historical?force_regenerate=true`
        : `/api/patient-summary/${patientId}/historical`;
      const response = await fetch(url);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed to fetch historical summary: ${response.status}`);
      }
      setHistoricalSummary(await response.json());
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      setHistoricalError(msg);
      setHistoricalSummary(null);
    } finally {
      setHistoricalLoading(false);
    }
  }, [patientId]);

  const fetchRealtimeSummary = useCallback(async () => {
    if (!sessionId) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setRealtimeLoading(true);
    setRealtimeError(null);
    try {
      const response = await fetch(`/api/patient-summary/${sessionId}/realtime`, { method: 'POST' });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed to fetch real-time summary: ${response.status}`);
      }
      setRealtimeSummary(await response.json());
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      setRealtimeError(msg);
      setRealtimeSummary(null);
    } finally {
      inFlightRef.current = false;
      setRealtimeLoading(false);
    }
  }, [sessionId]);

  const fetchClinicalRecommendation = useCallback(async (visitId: number) => {
    setClinicalLoading(true);
    setClinicalError(null);
    try {
      const response = await fetch(`/api/patient-summary/visits/${visitId}/clinical-recommendation`);
      if (response.status === 404) {
        setClinicalRecommendation(null);
        return;
      }
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed to fetch clinical recommendation: ${response.status}`);
      }
      setClinicalRecommendation(await response.json());
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      setClinicalError(msg);
      setClinicalRecommendation(null);
    } finally {
      setClinicalLoading(false);
    }
  }, []);

  const generateClinicalRecommendation = useCallback(async (visitId: number, sessionIdForGenerate?: string) => {
    setClinicalLoading(true);
    setClinicalError(null);
    try {
      const url = sessionIdForGenerate
        ? `/api/patient-summary/visits/${visitId}/clinical-recommendation?session_id=${encodeURIComponent(sessionIdForGenerate)}`
        : `/api/patient-summary/visits/${visitId}/clinical-recommendation`;
      const response = await fetch(url, { method: 'POST' });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed to generate clinical recommendation: ${response.status}`);
      }
      setClinicalRecommendation(await response.json());
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      setClinicalError(msg);
      setClinicalRecommendation(null);
    } finally {
      setClinicalLoading(false);
    }
  }, []);

  // ==========================================
  // EFFECTS
  // ==========================================

  // Fetch historical summary when patient changes
  useEffect(() => {
    if (patientId) fetchHistoricalSummary();
  }, [patientId, fetchHistoricalSummary]);

  // Clear realtime state when session ends; reset transcript gate
  useEffect(() => {
    if (!sessionId) {
      setRealtimeSummary(null);
      setRealtimeError(null);
      setHasTranscripts(false);
    }
  }, [sessionId]);

  // When a visit is ended (session transitions from a value -> undefined),
  // refresh historical summary so the just-ended visit appears immediately
  // in Previous Visits without a full page refresh.
  useEffect(() => {
    const prev = prevSessionIdRef.current;
    if (prev && !sessionId && patientId) {
      fetchHistoricalSummary(true);
    }
    prevSessionIdRef.current = sessionId;
  }, [sessionId, patientId, fetchHistoricalSummary]);

  // Auto-refresh realtime summary — gated on active session AND transcripts available
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!sessionId || !isSessionActive || !hasTranscripts) {
      setIsAutoRefreshing(false);
      return;
    }

    // First fetch immediately now that transcripts are available
    fetchRealtimeSummary();
    setIsAutoRefreshing(true);

    intervalRef.current = setInterval(() => {
      fetchRealtimeSummary();
    }, AUTO_REFRESH_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setIsAutoRefreshing(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, isSessionActive, hasTranscripts]);

  // ==========================================
  // RENDER
  // ==========================================

  return (
    <PatientSummaryContext.Provider
      value={{
        historicalSummary,
        historicalLoading,
        historicalError,
        fetchHistoricalSummary,
        realtimeSummary,
        realtimeLoading,
        realtimeError,
        fetchRealtimeSummary,
        clinicalRecommendation,
        clinicalLoading,
        clinicalError,
        fetchClinicalRecommendation,
        generateClinicalRecommendation,
        isAutoRefreshing,
        setHasTranscripts,
      }}
    >
      {children}
    </PatientSummaryContext.Provider>
  );
}

// ==========================================
// HOOK
// ==========================================

export function usePatientSummaryContext(): PatientSummaryContextValue {
  const ctx = useContext(PatientSummaryContext);
  if (!ctx) throw new Error('usePatientSummaryContext must be used inside <PatientSummaryProvider>');
  return ctx;
}
